import { DEVICE_WIDTH, type Device, EDITOR_FRAME_PATH } from "@aurumiq/cms-schema";
import { cn } from "@aurumiq/ui/lib/utils";
import type { RefObject } from "react";
import type { CmsPreviewToken } from "@/lib/types";

/**
 * Canvas iframe URL: the preview-token URL (`/api/draft?token=…&slug=…`) with `next` pointing at
 * the landing editor frame, so Draft Mode is enabled before the frame loads.
 */
export function frameSrc(token: CmsPreviewToken, slug: string): string {
  const next = EDITOR_FRAME_PATH(slug);
  try {
    const u = new URL(token.url);
    if (u.pathname.startsWith("/api/draft")) {
      u.searchParams.set("next", next);
      return u.toString();
    }
    return u.toString();
  } catch {
    return `${token.url}${token.url.includes("?") ? "&" : "?"}next=${encodeURIComponent(next)}`;
  }
}

export function Canvas({
  frameRef,
  src,
  device,
  ready,
  error,
}: {
  frameRef: RefObject<HTMLIFrameElement | null>;
  src: string | null;
  device: Device;
  ready: boolean;
  error?: string | null;
}) {
  const width = DEVICE_WIDTH[device];
  const desktop = device === "desktop";
  return (
    <main
      className="absolute inset-0 flex flex-col items-center overflow-hidden bg-muted bg-[radial-gradient(circle,rgba(20,33,61,0.14)_1px,transparent_1px)] bg-[size:20px_20px]"
      aria-label="Canvas"
    >
      {!desktop ? (
        <div className="mt-16 mb-1 font-mono text-[11px] text-muted-foreground uppercase tracking-wider">
          {device} · {width}px
        </div>
      ) : null}
      <div
        className={cn("relative flex min-h-0 flex-1 justify-center", desktop ? "w-full" : "mb-4")}
        style={{ width: desktop ? "100%" : `min(100% - 24px, ${width}px)` }}
      >
        <div
          className={cn(
            "relative h-full w-full overflow-hidden bg-white",
            !desktop && "rounded-[22px] border-[6px] border-navy shadow-2xl",
          )}
        >
          {src ? (
            <iframe
              ref={frameRef}
              title="Page canvas"
              src={src}
              className="h-full w-full border-0 bg-white"
            />
          ) : null}
          {!ready ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-white/90 text-muted-foreground text-sm">
              {error ? (
                <span className="text-danger">{error}</span>
              ) : (
                <span>{src ? "Loading canvas…" : "Requesting a preview token…"}</span>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </main>
  );
}
